import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2,
  Inbox,
  LogOut,
  RefreshCw,
  Trash2,
  MailOpen,
  Mail,
  Clock,
  PhoneCall,
  Tag,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, getToken, clearToken } from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminInbox() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleAuthError = useCallback(
    (err) => {
      if (err?.response?.status === 401) {
        clearToken();
        toast.error("Session expired", { description: "Please log in again." });
        navigate("/admin", { replace: true });
        return true;
      }
      return false;
    },
    [navigate]
  );

  const fetchMessages = useCallback(
    async (status = tab, silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await api.get("/admin/messages", { params: { status, limit: 100 } });
        setMessages(res.data.messages);
        setStats({ total: res.data.total, unread: res.data.unread });
      } catch (err) {
        if (!handleAuthError(err)) {
          toast.error("Could not load messages");
        }
      } finally {
        setLoading(false);
      }
    },
    [tab, handleAuthError]
  );

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    fetchMessages(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const openMessage = async (msg) => {
    setSelected(msg);
    if (!msg.read) {
      try {
        await api.patch(`/admin/messages/${msg.id}`, { read: true });
        setMessages((list) =>
          list.map((m) => (m.id === msg.id ? { ...m, read: true } : m))
        );
        setStats((s) => ({ ...s, unread: Math.max(0, s.unread - 1) }));
        setSelected((sel) => (sel && sel.id === msg.id ? { ...sel, read: true } : sel));
      } catch (err) {
        handleAuthError(err);
      }
    }
  };

  const toggleRead = async (msg) => {
    setActionLoading(true);
    try {
      const newRead = !msg.read;
      await api.patch(`/admin/messages/${msg.id}`, { read: newRead });
      setSelected((sel) => (sel && sel.id === msg.id ? { ...sel, read: newRead } : sel));
      toast.success(newRead ? "Marked as read" : "Marked as unread");
      fetchMessages(tab, true);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/messages/${deleteTarget.id}`);
      toast.success("Message deleted");
      setDeleteTarget(null);
      setSelected(null);
      fetchMessages(tab, true);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Could not delete message");
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="inbox-logo-link">
            <img src="/fcy-mark.png" alt="FCY IT Consultancy logo" className="w-9 h-9 object-contain" />
            <span className="text-sm font-semibold text-foreground hidden sm:block">
              Message Inbox
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchMessages(tab)}
              className="border-border bg-secondary/50 rounded-lg"
              data-testid="inbox-refresh-button"
            >
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-border bg-secondary/50 rounded-lg text-muted-foreground hover:text-foreground"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Log Out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 gap-4 mb-8 max-w-md">
          <div className="card-highlight rounded-2xl bg-card border border-border p-5" data-testid="inbox-total-stat">
            <p className="text-3xl font-semibold text-foreground">{stats.total}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              Total Messages
            </p>
          </div>
          <div className="card-highlight rounded-2xl bg-card border border-primary/30 p-5" data-testid="inbox-unread-stat">
            <p className="text-3xl font-semibold text-primary">{stats.unread}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              Unread
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-secondary border border-border rounded-xl" data-testid="inbox-filter-tabs">
            <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="inbox-tab-all">
              All
            </TabsTrigger>
            <TabsTrigger value="unread" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="inbox-tab-unread">
              Unread
            </TabsTrigger>
            <TabsTrigger value="read" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="inbox-tab-read">
              Read
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-6" data-testid="inbox-messages-table">
          {loading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-2xl bg-secondary/50" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="card-highlight rounded-2xl bg-card border border-border p-14 text-center" data-testid="inbox-empty-state">
              <Inbox className="w-10 h-10 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-foreground">No messages here</h3>
              <p className="text-sm text-muted-foreground mt-1.5">
                {tab === "all"
                  ? "When someone submits the contact form, their message will appear here."
                  : `No ${tab} messages at the moment.`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, i) => (
                <motion.button
                  key={msg.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  onClick={() => openMessage(msg)}
                  className={`w-full text-left card-highlight rounded-2xl bg-card border border-border p-5 hover:border-primary/35 transition-colors duration-200 ${
                    !msg.read ? "unread-bar" : ""
                  }`}
                  data-testid="inbox-message-row"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      {msg.read ? (
                        <MailOpen className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
                      ) : (
                        <Mail className="w-4 h-4 text-primary mt-1 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className={`text-sm truncate ${!msg.read ? "font-semibold text-foreground" : "text-foreground/80"}`}>
                          {msg.name}
                          <span className="text-muted-foreground font-normal"> · {msg.email}</span>
                        </p>
                        <p className="text-sm text-muted-foreground truncate mt-1 max-w-xl">
                          {msg.message}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                      <Badge variant="outline" className="border-primary/30 text-primary text-[11px] rounded-full max-w-[180px] truncate">
                        {msg.service}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono-meta">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="bg-card border-border max-w-xl rounded-2xl" data-testid="inbox-message-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-foreground text-lg flex items-center gap-2">
                  {selected.name}
                  {!selected.read && (
                    <Badge className="bg-primary/15 text-primary border border-primary/30 rounded-full text-[10px]">
                      NEW
                    </Badge>
                  )}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {selected.email}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Tag className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate">{selected.service}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4 text-primary shrink-0" />
                  {formatDate(selected.created_at)}
                </div>
                {selected.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <PhoneCall className="w-4 h-4 text-primary shrink-0" />
                    {selected.phone}
                  </div>
                )}
                {selected.budget && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Wallet className="w-4 h-4 text-primary shrink-0" />
                    {selected.budget}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-secondary/50 border border-border p-4 text-sm leading-relaxed text-foreground/90 max-h-60 overflow-y-auto whitespace-pre-wrap">
                {selected.message}
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actionLoading}
                  onClick={() => toggleRead(selected)}
                  className="border-border bg-secondary/50 rounded-lg"
                  data-testid="inbox-mark-read-button"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : selected.read ? (
                    <>
                      <Mail className="w-4 h-4 mr-2" /> Mark as Unread
                    </>
                  ) : (
                    <>
                      <MailOpen className="w-4 h-4 mr-2" /> Mark as Read
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteTarget(selected)}
                  className="rounded-lg bg-destructive/90 hover:bg-destructive"
                  data-testid="inbox-delete-message-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border rounded-2xl" data-testid="inbox-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete this message?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This will permanently remove the message from {deleteTarget?.name}. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-border bg-secondary/50 rounded-lg"
              data-testid="inbox-delete-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg"
              data-testid="inbox-delete-confirm-button"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
